import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getAuthUrl } from "@/lib/api";

export function Login() {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [searchParams] = useSearchParams();

  // Get redirect URL from query param (e.g., from docs.verkh.io)
  const redirectUrl = searchParams.get("redirect");

  function handleGoogleSignIn() {
    setIsRedirecting(true);
    // Store redirect URL in sessionStorage so callback can use it
    if (redirectUrl) {
      sessionStorage.setItem("auth_redirect", redirectUrl);
    }
    window.location.href = getAuthUrl("/auth/google");
  }

  function handleGithubSignIn() {
    setIsRedirecting(true);
    // Store redirect URL in sessionStorage so callback can use it
    if (redirectUrl) {
      sessionStorage.setItem("auth_redirect", redirectUrl);
    }
    window.location.href = getAuthUrl("/auth/github");
  }

  function handleMicrosoftSignIn() {
    setIsRedirecting(true);
    // Store redirect URL in sessionStorage so callback can use it
    if (redirectUrl) {
      sessionStorage.setItem("auth_redirect", redirectUrl);
    }
    window.location.href = getAuthUrl("/auth/microsoft");
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Header */}
      <header className="py-6 px-8">
        <Link to="/get-started" className="flex items-center gap-3 w-fit">
          <img
            src="/logo-dark.svg"
            alt="Verkh"
            className="w-9 h-9 rounded-lg"
          />
          <span className="text-white font-bold text-lg font-display leading-tight">Verkh</span>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-8">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2 font-display">
              Welcome back
            </h1>
            <p className="text-white/60">
              Sign in to your Verkh account
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleGoogleSignIn}
              disabled={isRedirecting}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white text-slate-900 rounded-xl font-medium hover:bg-white/90 transition-colors disabled:opacity-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </button>

            <button
              onClick={handleMicrosoftSignIn}
              disabled={isRedirecting}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#2F2F2F] text-white border border-white/10 rounded-xl font-medium hover:bg-[#3F3F3F] transition-colors disabled:opacity-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 21 21">
                <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
              </svg>
              Continue with Microsoft
            </button>

            <button
              onClick={handleGithubSignIn}
              disabled={isRedirecting}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-slate-800 text-white border border-white/10 rounded-xl font-medium hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
              Continue with GitHub
            </button>
          </div>

          {isRedirecting && (
            <div className="mt-6 flex items-center justify-center gap-2 text-white/60">
              <div className="animate-spin w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full" />
              <span>Redirecting...</span>
            </div>
          )}

          <p className="text-center text-white/50 text-sm mt-8">
            Don't have an account?{" "}
            <Link
              to="/get-started"
              className="text-white/70 hover:text-white font-medium"
            >
              Get started
            </Link>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 px-8 text-center text-white/40 text-sm">
        By signing in, you agree to our{" "}
        <a
          href="https://verkh.io/terms/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-white/60 hover:text-white/80"
        >
          Terms of Service
        </a>{" "}
        and{" "}
        <a
          href="https://verkh.io/privacy/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-white/60 hover:text-white/80"
        >
          Privacy Policy
        </a>
      </footer>
    </div>
  );
}
